import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { waitlistService, type WaitlistSignup, type LaunchSettings } from '../services/waitlistService'
import AppHeader from '../components/AppHeader'
import ProfileDropdown from '../components/ProfileDropdown'
import toast from 'react-hot-toast'
import {
  UserGroupIcon,
  CalendarIcon,
  EnvelopeIcon,
  ClockIcon,
  EyeIcon,
  ChartBarIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'

const AdminWaitlistPage = () => {
  const navigate = useNavigate()
  const [waitlistSignups, setWaitlistSignups] = useState<WaitlistSignup[]>([])
  const [launchSettings, setLaunchSettings] = useState<LaunchSettings | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isEditingDate, setIsEditingDate] = useState(false)
  const [newLaunchDate, setNewLaunchDate] = useState('')
  const [notes, setNotes] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [sourceFilter, setSourceFilter] = useState<string>('all')

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setIsLoading(true)
    try {
      const [signups, settings] = await Promise.all([
        waitlistService.getAllWaitlistSignups(),
        waitlistService.getLaunchSettings()
      ])
      
      setWaitlistSignups(signups)
      setLaunchSettings(settings)
      
      if (settings) {
        setNewLaunchDate(new Date(settings.launch_date).toISOString().slice(0, 16))
        setNotes(settings.notes || '')
      }
    } catch (error) {
      console.error('Error loading waitlist data:', error)
      toast.error('Failed to load waitlist data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleUpdateLaunchDate = async () => {
    if (!newLaunchDate) return

    try {
      const result = await waitlistService.updateLaunchDate(
        new Date(newLaunchDate).toISOString(),
        notes
      )
      
      if (result.success) {
        toast.success('Launch date updated successfully!')
        setIsEditingDate(false)
        await loadData()
      } else {
        toast.error(result.error || 'Failed to update launch date')
      }
    } catch (error) {
      toast.error('Failed to update launch date')
    }
  }

  const filteredSignups = waitlistSignups.filter(signup => {
    const matchesSearch = signup.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (signup.name?.toLowerCase().includes(searchTerm.toLowerCase()) || false)
    const matchesSource = sourceFilter === 'all' || signup.source === sourceFilter
    
    return matchesSearch && matchesSource
  })

  const stats = {
    total: waitlistSignups.length,
    recent: waitlistSignups.filter(s => 
      new Date(s.created_at) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
    ).length,
    sources: waitlistSignups.reduce((acc, s) => {
      acc[s.source] = (acc[s.source] || 0) + 1
      return acc
    }, {} as Record<string, number>)
  }

  const timeUntilLaunch = launchSettings ? 
    Math.max(0, new Date(launchSettings.launch_date).getTime() - Date.now()) : 0
  const daysUntilLaunch = Math.floor(timeUntilLaunch / (1000 * 60 * 60 * 24))

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background orbs */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <AppHeader title="Waitlist Management" subtitle="Manage launch countdown and waitlist signups">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2 px-3 py-1 bg-red-500/20 border border-red-500/30 rounded-full">
            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
            <span className="text-red-400 text-sm font-medium">Admin Panel</span>
          </div>
          <button
            onClick={() => navigate('/admin')}
            className="text-gray-400 hover:text-white transition-colors font-medium"
          >
            Admin Dashboard
          </button>
          <ProfileDropdown />
        </div>
      </AppHeader>

      {/* Main Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="card"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-blue-500/20 rounded-2xl flex items-center justify-center">
                    <UserGroupIcon className="w-6 h-6 text-blue-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.total}</p>
                    <p className="text-gray-400">Total Signups</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="card"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-emerald-500/20 rounded-2xl flex items-center justify-center">
                    <ChartBarIcon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{stats.recent}</p>
                    <p className="text-gray-400">This Week</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="card"
              >
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-orange-500/20 rounded-2xl flex items-center justify-center">
                    <CalendarIcon className="w-6 h-6 text-orange-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-white">{daysUntilLaunch}</p>
                    <p className="text-gray-400">Days Until Launch</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Launch Settings */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="card"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                  <ClockIcon className="w-5 h-5 text-blue-400" />
                  <span>Launch Settings</span>
                </h2>
                <button
                  onClick={() => setIsEditingDate(!isEditingDate)}
                  className="btn-secondary text-sm"
                >
                  {isEditingDate ? 'Cancel' : 'Edit Date'}
                </button>
              </div>

              {isEditingDate ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Launch Date & Time
                    </label>
                    <input
                      type="datetime-local"
                      value={newLaunchDate}
                      onChange={(e) => setNewLaunchDate(e.target.value)}
                      className="input-field w-full max-w-md"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                      Notes (optional)
                    </label>
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add notes about this launch date change..."
                      className="input-field w-full"
                      rows={3}
                    />
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={handleUpdateLaunchDate}
                      className="btn-primary"
                    >
                      <CheckIcon className="w-4 h-4 mr-2" />
                      Update Launch Date
                    </button>
                    <button
                      onClick={() => setIsEditingDate(false)}
                      className="btn-secondary"
                    >
                      <XMarkIcon className="w-4 h-4 mr-2" />
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-gray-300">
                    <span className="font-medium">Current Launch Date:</span>{' '}
                    {launchSettings ? 
                      new Date(launchSettings.launch_date).toLocaleString() : 
                      'Not set'
                    }
                  </p>
                  {launchSettings?.notes && (
                    <p className="text-gray-400">
                      <span className="font-medium">Notes:</span> {launchSettings.notes}
                    </p>
                  )}
                </div>
              )}
            </motion.div>

            {/* Waitlist Table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="card"
            >
              <div className="flex flex-col md:flex-row md:items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-white flex items-center space-x-2">
                  <EnvelopeIcon className="w-5 h-5 text-emerald-400" />
                  <span>Waitlist Signups</span>
                </h2>
                
                <div className="flex flex-col md:flex-row gap-4 mt-4 md:mt-0">
                  <input
                    type="text"
                    placeholder="Search by email or name..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-field w-full md:w-64"
                  />
                  <select
                    value={sourceFilter}
                    onChange={(e) => setSourceFilter(e.target.value)}
                    className="input-field w-full md:w-40"
                  >
                    <option value="all">All Sources</option>
                    {Object.keys(stats.sources).map(source => (
                      <option key={source} value={source}>
                        {source} ({stats.sources[source]})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Email</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Name</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Source</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Date</th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredSignups.map((signup, index) => (
                      <motion.tr
                        key={signup.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="border-b border-white/5 hover:bg-white/5"
                      >
                        <td className="py-3 px-4 text-white">{signup.email}</td>
                        <td className="py-3 px-4 text-gray-300">{signup.name || 'N/A'}</td>
                        <td className="py-3 px-4">
                          <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded-full text-xs">
                            {signup.source}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-gray-400 text-sm">
                          {new Date(signup.created_at).toLocaleDateString()}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(signup.email)
                              toast.success('Email copied to clipboard')
                            }}
                            className="text-gray-400 hover:text-white transition-colors"
                            title="Copy email"
                          >
                            <EyeIcon className="w-4 h-4" />
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
                
                {filteredSignups.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-gray-400">No waitlist signups found.</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </div>
    </div>
  )
}

export default AdminWaitlistPage 